import { useCallback, useEffect, useRef, useState } from 'react';
import { type DraftAttachment, revokeBlobPreview } from '../utils/attachment-normalize';

export interface UseClawInputAttachmentsReturn {
  attachments: DraftAttachment[];
  /** 同步 ref，便于在 effect 清理函数 / 异步 callback 里读到最新数组。 */
  attachmentsRef: React.MutableRefObject<DraftAttachment[]>;
  setAttachments: React.Dispatch<React.SetStateAction<DraftAttachment[]>>;
  /** 按 file_path 追加，已存在则跳过；用于 quick prompt 异步补传后回填。 */
  appendUniqueByFilePath: (incoming: DraftAttachment[]) => void;
  /** 过滤掉命中 predicate 的附件，并撤销其 blob preview，保持卡片 / blob 一致。 */
  removeAttachmentsMatching: (shouldRemove: (file: DraftAttachment) => boolean) => void;
  /** 一次性清空：撤销 blob preview，清掉 quickPreview backfill 状态由调用方自己处理。 */
  clearAttachments: () => void;
}

/**
 * ChatInput 草稿附件状态：列表、ref、增 / 删 / 清空。
 * 不在这里耦合编辑器 mention 同步，调用方按需再调 mention 删除。
 */
export const useClawInputAttachments = (): UseClawInputAttachmentsReturn => {
  const [attachments, setAttachments] = useState<DraftAttachment[]>([]);
  const attachmentsRef = useRef<DraftAttachment[]>(attachments);
  // 渲染期同步 ref，比 useEffect 早一帧，能让随后的 callback 立刻读到最新值。
  attachmentsRef.current = attachments;

  // unmount 时撤销所有 blob preview；用 ref 是因为 effect 清理函数无法读到最新 state。
  useEffect(
    () => () => {
      attachmentsRef.current.forEach(revokeBlobPreview);
    },
    [],
  );

  const appendUniqueByFilePath = useCallback((incoming: DraftAttachment[]) => {
    if (!incoming.length) return;
    setAttachments((prev) => {
      const existed = new Set(prev.map((f) => f.file_path));
      const fresh = incoming.filter((f) => !existed.has(f.file_path));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, []);

  const removeAttachmentsMatching = useCallback((shouldRemove: (file: DraftAttachment) => boolean) => {
    setAttachments((prev) => {
      const next: DraftAttachment[] = [];
      for (const file of prev) {
        if (shouldRemove(file)) {
          revokeBlobPreview(file);
          continue;
        }
        next.push(file);
      }
      return next;
    });
  }, []);

  const clearAttachments = useCallback(() => {
    setAttachments((prev) => {
      prev.forEach(revokeBlobPreview);
      return [];
    });
  }, []);

  return {
    attachments,
    attachmentsRef,
    setAttachments,
    appendUniqueByFilePath,
    removeAttachmentsMatching,
    clearAttachments,
  };
};
